import { Routes } from "@/shared/constants/Routing";
import type { IndustryPreset } from "@/shared/types/industryPreset";
import type { OnboardIntent } from "@/shared/types/onboard.types";

export type LeadIntent = {
    tariff?: string;
    requestType?: string;
    pagePath?: string;
    industryPreset?: IndustryPreset;
    /** CRO / marketing attribution tag, e.g. horeca_modal */
    leadSource?: string;
    updatedAt?: number;
    onboard?: OnboardIntent;
};

const STORAGE_KEY = "tezam:lead-intent";
const EVENT_NAME = "tezam:lead-intent";

const TELEGRAM_PATHS = new Set<string>([
    Routes.TMA,
    Routes.TELEGRAM_SHOP,
    Routes.TELEGRAM_BOT_BUILDER,
    Routes.TELEGRAM_BOT_DEVELOPMENT,
    Routes.TELEGRAM_CRM,
    Routes.TELEGRAM_HORECA,
    Routes.TELEGRAM_BEAUTY,
    Routes.TELEGRAM_SERVICES,
    Routes.TELEGRAM_EVENTS,
    Routes.TELEGRAM_SERVICE_COMPANIES,
    Routes.DIGITAL_SHOWCASE,
    Routes.TELEGRAM_AUTOMATION,
]);

export function getRequestTypeByPathname(pathname: string): string {
    if (pathname === "/start" || pathname.startsWith("/solutions/")) return "Запуск платформы TEZAM";
    if (pathname === Routes.MAX_MINI_APP) return "Разработка MAX Mini App";
    if (pathname === Routes.CONTEXTUAL_ADS) return "Контекстная реклама";
    if (pathname === Routes.WEBSITES_DEVELOPMENT) return "Разработка сайтов";
    if (pathname === Routes.PRODUCT_SUPPORT) return "Продуктовое сопровождение";
    if (pathname === Routes.TURNKEY_LAUNCH) return "Запуск и настройка TEZAM под ключ";
    if (TELEGRAM_PATHS.has(pathname)) return "Разработка Telegram Mini App";
    return "Разработка в целом";
}

export function readLeadIntent(): LeadIntent {
    if (typeof window === "undefined") return {};
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    try {
        return JSON.parse(raw) as LeadIntent;
    } catch {
        return {};
    }
}

export function writeLeadIntent(next: LeadIntent): LeadIntent {
    const payload: LeadIntent = {
        ...readLeadIntent(),
        ...next,
        updatedAt: Date.now(),
    };

    if ("industryPreset" in next && !next.industryPreset) {
        delete payload.industryPreset;
    }

    if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: payload }));
    }

    return payload;
}

export function subscribeLeadIntent(listener: (intent: LeadIntent) => void) {
    if (typeof window === "undefined") return () => undefined;

    const handler = (event: Event) => {
        const custom = event as CustomEvent<LeadIntent>;
        listener(custom.detail ?? readLeadIntent());
    };

    window.addEventListener(EVENT_NAME, handler as EventListener);

    return () => {
        window.removeEventListener(EVENT_NAME, handler as EventListener);
    };
}
